import { BadgeCheck, FileCheck2, Microscope, Camera, Wrench, FolderCheck } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const QA_ITEMS = [
  { icon: BadgeCheck, title: "Supplier Audit & Verification", desc: "Legal standing, capacity and compliance checked before any order is placed." },
  { icon: FileCheck2, title: "Golden Sample Approval", desc: "Nothing enters bulk production without a written, signed-off reference sample." },
  { icon: Camera, title: "In-Line & Pre-Shipment Inspection", desc: "AQL-based checks with photographic reports shared before dispatch is authorised." },
  { icon: Microscope, title: "Testing & Documentation", desc: "Third-party laboratory testing and certification arranged where required." },
  { icon: Wrench, title: "Corrective Action & Claims", desc: "Documented CAPA process and structured claim support on any deviation." },
  { icon: FolderCheck, title: "Full Documentation Pack", desc: "A complete documentation pack is issued with every shipment." },
];

const NEXGEN_HOLDS = [
  { code: "IEC", name: "Importer Exporter Code", issuer: "DGFT, Government of India" },
  { code: "GST", name: "Goods & Services Tax", issuer: "Registered in India" },
];

const FACTORY_VERIFIED = [
  { name: "ISO 9001", desc: "Quality management" },
  { name: "OEKO-TEX Standard 100", desc: "Textile safety" },
  { name: "SEDEX / SMETA", desc: "Ethical trade audit" },
  { name: "BSCI / WRAP", desc: "Social compliance" },
];

export const Quality = () => (
  <section id="quality" className="relative py-28 lg:py-40 bg-white" data-testid="quality-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal>
        <SectionTag index="05" label="Quality Assurance" />
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-24">
        <Reveal className="lg:col-span-5">
          <div className="spotlight-frame relative rounded-2xl border border-[#2E7FE0]/15 overflow-hidden h-[420px] lg:h-full min-h-[420px]" data-testid="quality-image-frame">
            <img
              src="/quality-inspection-v2.webp"
              alt="Quality inspector checking fabric rolls and samples at source factory"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#051024]/90 via-transparent to-transparent z-[3]" />
            <div className="absolute bottom-0 left-0 right-0 z-[4] p-8">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#A1B0C8]">
                Quality controlled at source — not at the port
              </p>
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-[#0A1B33] mb-12" data-testid="quality-heading">
              Inspected at every stage, <span className="text-[#2E7FE0]">documented at every step.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-[#2E7FE0]/12 border border-[#2E7FE0]/12 rounded-2xl overflow-hidden">
            {QA_ITEMS.map((q, i) => (
              <Reveal key={q.title} delay={i * 0.05} className="h-full">
                <div className="bg-white p-8 h-full" data-testid={`qa-item-${i}`}>
                  <q.icon className="w-6 h-6 text-[#2E7FE0] mb-5" />
                  <h3 className="font-display font-semibold text-lg text-[#0A1B33] mb-3 leading-snug">{q.title}</h3>
                  <p className="text-[#54678A] text-sm leading-relaxed">{q.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <Reveal>
        <h3 className="font-display font-semibold text-3xl text-[#0A1B33] mb-10" data-testid="certifications-heading">
          Certifications & compliance
        </h3>
      </Reveal>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 flex flex-col gap-6" data-testid="nexgen-holds-card">
          <Reveal>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#2E7FE0]">NexGen holds</p>
          </Reveal>
          {NEXGEN_HOLDS.map((c, i) => (
            <Reveal key={c.code} delay={i * 0.08} className="flex-1">
              <div
                className="glass-panel rounded-2xl px-6 py-5 h-full flex items-center gap-5 hover:border-[#2E7FE0]/50 transition-colors duration-500"
                data-testid={`nexgen-cert-${i}`}
              >
                <span className="shrink-0 w-14 h-14 rounded-xl bg-[#2E7FE0]/12 border border-[#2E7FE0]/30 flex items-center justify-center font-display font-bold text-base text-[#2E7FE0]">
                  {c.code}
                </span>
                <div>
                  <h4 className="font-display font-semibold text-lg text-[#0A1B33] mb-1">{c.name}</h4>
                  <p className="text-[#54678A] text-sm">{c.issuer}</p>
                </div>
                <BadgeCheck className="w-5 h-5 text-[#2E7FE0] ml-auto shrink-0" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12} className="lg:col-span-7">
          <div className="glass-panel rounded-2xl p-8 h-full flex flex-col" data-testid="factory-verified-card">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#2E7FE0] mb-6">
              Verified at partner factories
            </p>
            <div className="grid sm:grid-cols-2 gap-3 flex-1 content-center">
              {FACTORY_VERIFIED.map((c, i) => (
                <div
                  key={c.name}
                  className="bg-[#F8FAFE] border border-[#2E7FE0]/15 rounded-lg px-5 py-4"
                  data-testid={`factory-cert-${i}`}
                >
                  <p className="font-display font-semibold text-[#0A1B33] text-sm">{c.name}</p>
                  <p className="text-[#54678A] text-xs mt-1">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal className="mt-8">
        <p className="text-[#54678A]/70 text-xs leading-relaxed max-w-3xl" data-testid="cert-disclaimer">
          Certifications are held by the manufacturing units we work with and are scope- and
          product-specific. Copies of the relevant certificates are shared with the buyer before
          order confirmation.
        </p>
      </Reveal>
    </div>
  </section>
);
